import { MessageOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import { type RootState } from '@reducers';
import { Badge, Space, theme, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';
import {
  type CSSProperties,
  memo,
  type ReactElement,
  useCallback,
  useMemo,
} from 'react';
import { useSelector } from 'react-redux';

import { EDateFormat } from '../../fragments/date-format';
import { useAppDispatch } from '../../hooks/use-app-dispatch';
import { isLeadEngaged, setActiveLead } from '../../reducers/messenger/reducer';
import {
  ELeadFilter,
  type LeadMessenger,
} from '../../reducers/messenger/types';
import { SPACING } from '../../theme/tokens';

const styleListItem: CSSProperties = {
  cursor: 'pointer',
  padding: SPACING.md,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: SPACING.xs,
};

const MessengerListItem = memo(function MessengerListItem({
  lead,
  isSelected,
  onSelect,
}: {
  lead: LeadMessenger;
  isSelected: boolean;
  onSelect: (uuid: string) => void;
}): ReactElement {
  const { token } = theme.useToken();

  const styleListItemActivity: CSSProperties = {
    backgroundColor: token.colorFillSecondary,
  };

  if (isSelected) {
    styleListItemActivity.backgroundColor = token.colorSuccessBg;
  } else if (lead.unread && lead.unread > 0 && lead.isActive) {
    styleListItemActivity.backgroundColor = token.colorInfoBg;
  } else if (lead.unread) {
    styleListItemActivity.backgroundColor = token.colorWarningBg;
  } else if (lead.isActive) {
    styleListItemActivity.backgroundColor = token.colorWarningBgHover;
  }

  return (
    <div
      onClick={(): void => onSelect(lead.uuid)}
      style={{ ...styleListItem, ...styleListItemActivity }}
    >
      <Space size={SPACING.sm} align="center">
        <Typography.Text strong>{lead.uuid.slice(0, 8)}</Typography.Text>
        {lead.hasMessages && (
          <Tooltip title="Sent a message">
            <MessageOutlined />
          </Tooltip>
        )}
        {lead.hasBasketProducts && (
          <Tooltip title="Has products in basket">
            <ShoppingCartOutlined />
          </Tooltip>
        )}
      </Space>
      <Typography.Text type="secondary">
        <Space size={SPACING.lg} align="start">
          <Badge count={lead.unread ?? 0} showZero />
          {dayjs((lead.lastActivity || 0) * 1000).format(EDateFormat.DATE_TIME)}
        </Space>
      </Typography.Text>
    </div>
  );
});

function MessengerList({
  filter = ELeadFilter.ENGAGED,
}: {
  filter?: ELeadFilter;
} = {}): ReactElement {
  const dispatch = useAppDispatch();
  const messengerLeadUUID = useSelector(
    (s: RootState) => s.Messenger.lead.uuid,
  );
  const messengerLeads = useSelector((s: RootState) => s.Messenger.leads);

  const handleSelect = useCallback(
    (uuid: string): void => {
      dispatch(setActiveLead(uuid));
    },
    [dispatch],
  );

  const visibleLeads = useMemo(
    (): LeadMessenger[] =>
      filter === ELeadFilter.ALL
        ? (messengerLeads ?? [])
        : (messengerLeads ?? []).filter(
            (lead: LeadMessenger): boolean =>
              isLeadEngaged(lead) || lead.uuid === messengerLeadUUID,
          ),
    [filter, messengerLeads, messengerLeadUUID],
  );

  return (
    <div>
      {visibleLeads.map((lead: LeadMessenger): ReactElement => (
        <MessengerListItem
          key={lead.uuid}
          lead={lead}
          isSelected={messengerLeadUUID === lead.uuid}
          onSelect={handleSelect}
        />
      ))}
    </div>
  );
}

export default MessengerList;
